import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Corrections() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/corrections", { params: filter ? { status: filter } : {} });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const decide = async (c, status) => {
    const note = window.prompt(status === "approved" ? "Optional note for approval:" : "Reason for declining (optional):") || "";
    try {
      await api.patch(`/corrections/${c.id}`, { status, supervisor_note: note });
      toast.success(status === "approved" ? "Correction approved" : "Correction declined");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const canDecide = user?.role === "admin" || user?.role === "supervisor";

  return (
    <div className="px-4 md:px-10 py-8 max-w-6xl mx-auto" data-testid="corrections-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Workflow</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Correction Requests</h1>
          <p className="text-zinc-600 mt-1 text-sm">{items.length} requests visible to you.</p>
        </div>
        <div className="flex gap-2">
          {["", "pending", "approved", "declined"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
              data-testid={`filter-${s || "all"}`}
            >
              {s || "all"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Employee</Th>
              <Th>Date</Th>
              <Th>Reason</Th>
              <Th>Note</Th>
              <Th>Requested</Th>
              <Th>Status</Th>
              {canDecide && <Th className="text-right">Action</Th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {items.length === 0 && (
              <tr><td colSpan={canDecide ? 7 : 6} className="text-center text-zinc-500 py-10">No requests</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} data-testid={`correction-row-${c.id}`}>
                <Td>
                  <div className="font-semibold text-zinc-950">{c.user_name}</div>
                  <div className="text-xs text-zinc-500">{c.user_email}</div>
                </Td>
                <Td className="font-mono">{c.date}</Td>
                <Td className="text-zinc-700 capitalize">{c.reason.replace(/_/g, " ")}</Td>
                <Td className="text-zinc-700 max-w-xs">{c.note}</Td>
                <Td className="font-mono text-xs">
                  {c.requested_clock_in && <div>IN {c.requested_clock_in}</div>}
                  {c.requested_clock_out && <div>OUT {c.requested_clock_out}</div>}
                </Td>
                <Td><StatusBadge status={c.status} /></Td>
                {canDecide && (
                  <Td className="text-right">
                    {c.status === "pending" ? (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => decide(c, "approved")}
                          className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-xs rounded-sm hover:bg-emerald-500"
                          data-testid={`approve-${c.id}`}
                        >
                          <CheckCircle size={12} weight="bold" /> Approve
                        </button>
                        <button
                          onClick={() => decide(c, "declined")}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs rounded-sm hover:bg-red-500"
                          data-testid={`decline-${c.id}`}
                        >
                          <XCircle size={12} weight="bold" /> Decline
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
